import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';


export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [savedCalculations, setSavedCalculations] = useState([]);
    const [remoteCalculations, setRemoteCalculations] = useState([]);

    const API_URL = 'https://uvalue-calculator.nimbus.sugarprojects.com/';


    const syncRemoteCalculationsToLocal = async () => {
        if (!Array.isArray(remoteCalculations) || remoteCalculations.length === 0) return;

        const savedIds = savedCalculations.map(calc => calc.id);

        const newCalcs = remoteCalculations
            .map(remote => remote.data)
            .filter(remoteData => remoteData && !savedIds.includes(remoteData.id))
            .map(remoteData => ({
                id: remoteData.id,
                name: remoteData.name,
                date: remoteData.date,
                formData: remoteData.formData,
                product: remoteData.product,
                projectId: remoteData.projectId,
                result: remoteData.result
            }));

        if (newCalcs.length === 0) return;

        const updatedCalcs = [...savedCalculations, ...newCalcs];

        try {
            localStorage.setItem('savedCalculations', JSON.stringify(updatedCalcs));
            setSavedCalculations(updatedCalcs);
            //console.log(`✅ Synced ${newCalcs.length} remote calculation(s) to local.`);
        } catch (e) {
            console.error('❌ Failed to sync remote calculations:', e);
        }
    };

    const syncCalculationToRemote = async (calculationId) => {
        if (!isLoggedIn) {
            console.warn("User must be logged in to sync.");
            return;
        }

        const calculation = savedCalculations.find(c => c.id === calculationId);
        if (!calculation) {
            console.warn("Calculation not found:", calculationId);
            return;
        }

        try {
            await axios.post(`${API_URL}/wp-json/uvalue/v1/save/`, {
            name: calculation.name,
            ...calculation,
            }, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            });

            // Optionally refresh remoteCalculations
            fetchRemoteCalculations();

            //console.log('Calculation synced:', calculationId);
        } catch (error) {
            console.error('Failed to sync calculation:', error?.response?.data || error.message);
        }
    };



    const generateProductUrl = (product, formData) => {
        const baseUrl = "https://uvaluecalculator.mannokbuild.com/";
        let Url = `${baseUrl}?product=${product?.id || ''}`;
        Object.keys(formData || {}).forEach(key => {
            if (formData[key] !== undefined && formData[key] !== "") {
            Url += `&${key}=${encodeURIComponent(formData[key])}`;
            }
        });
        return Url;
    };

    useEffect(() => {
        if (user) {
            fetchRemoteCalculations();
            syncRemoteCalculationsToLocal();
        }
    }, [user]);

    useEffect(() => {
        if (user) {
            syncRemoteCalculationsToLocal();
        }
    }, [remoteCalculations]);

    //useEffect(() => {
    //    if (savedCalculations) {
    //        console.log("savedCalculations:", savedCalculations);
    //    }
    //}, [savedCalculations]);


    const fetchRemoteCalculations = async () => {
        if (!token) return;
        try {
            const response = await axios.get(`${API_URL}/wp-json/uvalue/v1/get/`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            });
            if (Array.isArray(response.data)) {
                setRemoteCalculations(response.data);
            } else {
            console.warn('Unexpected response from backend', response.data);
            }
        } catch (error) {
            console.error('Failed to fetch remote calculations:', error.response?.data || error.message);
        }
    };

    const saveCalculation = async (name, product, formData, result, projectId = null) => {
        if (!product || !result || !formData) return;

        const newCalculation = {
        id: uuidv4(),
        name,
        date: new Date().toISOString(),
        product,
        formData,
        result,
        projectId,
        };

        const updated = [...savedCalculations, newCalculation];
        setSavedCalculations(updated);
        localStorage.setItem('savedCalculations', JSON.stringify(updated));

        if (isLoggedIn) {
        try {
            await axios.post(
            `${API_URL}/wp-json/uvalue/v1/save/`,
            { name, ...newCalculation },
            {
                headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
                },
            }
            );
            await fetchRemoteCalculations();
        } catch (error) {
            console.error('Failed to sync with WordPress:', error?.response?.data || error.message);
        }
        }
    };

    const deleteSavedCalculation = async (id) => {
        // Find if it's in remote
        const remoteMatch = remoteCalculations.find(remote => remote.data?.id === id);

        if (remoteMatch) {
            await deleteRemoteCalculation(remoteMatch.id);
        }

        // Delete locally
        const updated = savedCalculations.filter(c => c.id !== id);
        setSavedCalculations(updated);
        localStorage.setItem('savedCalculations', JSON.stringify(updated));
    };

    const deleteRemoteCalculation = async (wordpressId) => {
        if (!token) {
            window.alert('You must be logged in to delete a remote calculation.');
            return;
        }

        try {
            await axios.post(`${API_URL}/wp-json/uvalue/v1/delete/`, {
            id: wordpressId,
            }, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            });

            setRemoteCalculations(prev =>
            prev.filter(c => c.id !== wordpressId)
            );

            window.alert('Remote calculation deleted.');
        } catch (error) {
            console.error('Error deleting remote calculation:', error.response?.data || error.message);
            window.alert('Failed to delete remote calculation.');
        }
    };



    // Restore from localStorage on load
    useEffect(() => {
        const storedToken = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');
        if (storedToken) {
            setToken(storedToken);
        }
        if (storedUser) {
            setUser(JSON.parse(storedUser));
        }

        const storedCalculations = localStorage.getItem('savedCalculations');
        if (storedCalculations) {
            setSavedCalculations(JSON.parse(storedCalculations));
        }

        const handleOffline = () => setIsOnline(false);
        const handleOnline = () => setIsOnline(true);
        window.addEventListener('offline', handleOffline);
        window.addEventListener('online', handleOnline);

        return () => {
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('online', handleOnline);
        };
    }, []);

    // Save to localStorage when updated
    useEffect(() => {
        if (token) {
            localStorage.setItem('token', token);
        } else {
            localStorage.removeItem('token');
        }
        if (user) {
            localStorage.setItem('user', JSON.stringify(user));
        } else {
            localStorage.removeItem('user');
        }
    }, [token, user]);

    const login = (userData, tokenData) => {
        setUser(userData);
        setToken(tokenData);
    };

    const logout = () => {
        setUser(null);
        setToken(null);
    };

    const isLoggedIn = !!token;

    return (
        <AuthContext.Provider
        value={{
            user,
            token,
            isLoggedIn,
            isOnline,
            login,
            logout,
            savedCalculations,
            remoteCalculations,
            saveCalculation,
            fetchRemoteCalculations,
            deleteSavedCalculation,
            deleteRemoteCalculation,
            generateProductUrl,
            syncCalculationToRemote
        }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useData = () => useContext(AuthContext);